import { useState } from 'react';
import DataTable from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import SearchBar from '../../components/SearchBar';
import { useToast } from '../../context/ToastContext';
import { useMessagingTokens, useUsers, useSendNotification, useGenerateCopy } from '../../hooks/useMessaging';

const TONES = ['exciting', 'friendly', 'urgent', 'professional', 'playful'];

const LIMIT = 20;

const MODES = [
  { key: 'single', label: 'Single token', help: 'Send to one FCM device token (admin auth).' },
  { key: 'user', label: 'User', help: 'Send to a specific user by their user id.' },
  { key: 'device', label: 'Device token', help: 'Authenticate as a device via X-Device-Token.' },
  { key: 'broadcast', label: 'Broadcast', help: 'Send to all registered devices.' },
];

const short = (t) => (t ? `${t.slice(0, 14)}…${t.slice(-6)}` : '—');

// Username / email may arrive nested under `user` or as flat fields.
const userName = (r) => r.user?.name ?? r.user_name ?? r.name ?? '';
const userEmail = (r) => r.user?.email ?? r.user_email ?? r.email ?? '';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleString();
}

export default function NotificationsPage() {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState('single');
  const [form, setForm] = useState({ token: '', userId: '', title: '', body: '', image: '', link: '', data: '', deviceToken: '' });
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [ai, setAi] = useState({ prompt: '', tone: 'exciting' });

  const { data, isLoading, error } = useMessagingTokens({ page, limit: LIMIT });
  const rows = data?.data || [];

  // Users list (targets for the "User" send mode).
  const { data: usersData, isLoading: usersLoading, error: usersError } =
    useUsers({ page: userPage, limit: LIMIT, search: userSearch });
  const users = usersData?.data || [];

  const sendM = useSendNotification({
    onSuccess: (res) => {
      toast.success(res?.message || 'Notification sent');
      setForm((f) => ({ ...f, title: '', body: '', image: '', link: '', data: '', userId: '' }));
    },
    onError: (e) => toast.error(e.message || 'Send failed'),
  });

  const genM = useGenerateCopy({
    onSuccess: (res) => {
      const copy = res?.data || {};
      setForm((f) => {
        // Merge a returned `type` into the data-JSON field without clobbering existing keys.
        let data = f.data;
        if (copy.type) {
          let obj = {};
          if (f.data.trim()) { try { obj = JSON.parse(f.data); } catch { obj = {}; } }
          obj.type = copy.type;
          data = JSON.stringify(obj, null, 2);
        }
        return {
          ...f,
          title: copy.title ?? f.title,
          body: copy.body ?? f.body,
          link: copy.link ?? f.link,
          image: copy.image ?? f.image,
          data,
        };
      });
      toast.success('Copy generated');
    },
    onError: (e) => toast.error(e.message || 'Generation failed'),
  });

  const generate = (e) => {
    e.preventDefault();
    if (!ai.prompt.trim()) { toast.error('Describe the notification first'); return; }
    genM.mutate({ prompt: ai.prompt.trim(), tone: ai.tone });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const applyToken = (t) => { setMode('single'); setForm((f) => ({ ...f, token: t })); };
  const applyUser = (u) => { setMode('user'); setForm((f) => ({ ...f, userId: String(u.id ?? '') })); };

  const submit = (e) => {
    e.preventDefault();

    // Optional data JSON (targeted modes only).
    let dataObj;
    if (mode !== 'broadcast' && form.data.trim()) {
      try { dataObj = JSON.parse(form.data); }
      catch { toast.error('Data must be valid JSON'); return; }
    }

    const payload = { title: form.title.trim(), body: form.body.trim() };
    if (form.link.trim()) payload.link = form.link.trim();
    if (form.image.trim()) payload.image = form.image.trim();
    if (mode !== 'broadcast' && dataObj) payload.data = dataObj;

    if (mode === 'single' || mode === 'device') {
      if (!form.token.trim()) { toast.error('FCM token is required'); return; }
      payload.token = form.token.trim();
    }
    if (mode === 'user') {
      if (!form.userId.trim()) { toast.error('Select a user first'); return; }
      payload.user_id = form.userId.trim();
    }
    if (mode === 'device' && !form.deviceToken.trim()) {
      toast.error('Device JWT is required for this method'); return;
    }

    sendM.mutate({
      payload,
      deviceToken: mode === 'device' ? form.deviceToken.trim() : undefined,
    });
  };

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'device_id', header: 'Device' },
    {
      key: 'user', header: 'User', render: (r) => {
        const name = userName(r);
        const email = userEmail(r);
        if (name || email) {
          return (
            <span className="user-cell">
              <span className="user-cell__name">{name || email}</span>
              {name && email && <span className="user-cell__email">{email}</span>}
            </span>
          );
        }
        return r.user_id != null ? `#${r.user_id}` : <span className="muted">guest</span>;
      },
    },
    { key: 'fcm_token', header: 'FCM token', render: (r) => <code className="token" title={r.fcm_token}>{short(r.fcm_token)}</code> },
    { key: 'updated_at', header: 'Updated', render: (r) => fmtDate(r.updated_at) },
  ];

  const usersColumns = [
    { key: 'id', header: 'ID' },
    { key: 'name', header: 'Name', render: (r) => userName(r) || <span className="muted">—</span> },
    { key: 'email', header: 'Email', render: (r) => userEmail(r) || <span className="muted">—</span> },
    { key: 'phone', header: 'Phone', render: (r) => r.phone ?? r.mobile ?? <span className="muted">—</span> },
  ];

  const activeMode = MODES.find((m) => m.key === mode);

  return (
    <div className="page">
      <div className="page__header"><h1>Notifications</h1></div>

      <div className="notif-row">
      <div className="card">
        <h2 className="card__title">Send notification</h2>
        <div className="seg" style={{ marginBottom: 16 }}>
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`seg__btn${mode === m.key ? ' seg__btn--active' : ''}`}
              onClick={() => setMode(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <p className="field__help" style={{ marginTop: -8, marginBottom: 14 }}>{activeMode.help}</p>

        <form className="form" onSubmit={submit}>
          {mode === 'device' && (
            <label className="field">
              <span>Device JWT (X-Device-Token)</span>
              <input value={form.deviceToken} onChange={set('deviceToken')} placeholder="device JWT" />
            </label>
          )}
          {(mode === 'single' || mode === 'device') && (
            <label className="field">
              <span>FCM device token</span>
              <input value={form.token} onChange={set('token')} placeholder="paste or click a token below" required />
            </label>
          )}
          {mode === 'user' && (
            <label className="field">
              <span>Target user id</span>
              <input value={form.userId} onChange={set('userId')} placeholder="pick a user below" required />
            </label>
          )}
          <label className="field">
            <span>Title</span>
            <input value={form.title} onChange={set('title')} required />
          </label>
          <label className="field">
            <span>Body</span>
            <textarea value={form.body} onChange={set('body')} rows={2} required />
          </label>
          <label className="field">
            <span>Deep link</span>
            <input value={form.link} onChange={set('link')} placeholder="travelens://itinerary/123" />
          </label>
          <label className="field">
            <span>Image URL</span>
            <input value={form.image} onChange={set('image')} placeholder="https://…/banner.png" />
          </label>
          {form.image.trim() && (
            <div className="notif-preview">
              <img src={form.image.trim()} alt="preview" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
          )}
          {mode !== 'broadcast' && (
            <label className="field">
              <span>Data (JSON, optional)</span>
              <textarea value={form.data} onChange={set('data')} rows={2} placeholder='{"screen":"explore","city":"Goa"}' />
            </label>
          )}
          <div className="form__actions">
            <button className="btn btn--primary" type="submit" disabled={sendM.isPending}>
              {sendM.isPending ? 'Sending…' : mode === 'broadcast' ? 'Broadcast to all' : 'Send'}
            </button>
          </div>
        </form>
      </div>

      <div className="card notif-ai">
        <h2 className="card__title">✨ AI copy generator</h2>
        <p className="field__help" style={{ marginTop: -8, marginBottom: 14 }}>
          Describe the notification and generate a title &amp; body — the result fills the form on the left.
        </p>
        <form className="form" onSubmit={generate}>
          <label className="field">
            <span>What's it about?</span>
            <textarea
              value={ai.prompt}
              onChange={(e) => setAi((a) => ({ ...a, prompt: e.target.value }))}
              rows={3}
              placeholder="Weekend getaway deals near Goa, 30% off hotels"
            />
          </label>
          <label className="field">
            <span>Tone</span>
            <select value={ai.tone} onChange={(e) => setAi((a) => ({ ...a, tone: e.target.value }))}>
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <div className="form__actions">
            <button className="btn btn--primary" type="submit" disabled={genM.isPending}>
              {genM.isPending ? 'Generating…' : 'Generate copy'}
            </button>
          </div>
        </form>
      </div>
      </div>

      <h2 className="detail__subhead">Registered devices</h2>
      <DataTable
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        error={error}
        emptyText="No FCM tokens registered."
        actions={(row) => (
          <button className="btn btn--sm" onClick={() => applyToken(row.fcm_token)}>Use token</button>
        )}
      />

      {data && data.total != null && (
        <Pagination page={page} limit={LIMIT} total={data.total} onPage={setPage} />
      )}

      <h2 className="detail__subhead">All users</h2>
      <div className="page__toolbar">
        <SearchBar
          value={userSearch}
          onSearch={(v) => { setUserSearch(v); setUserPage(1); }}
          placeholder="Search users…"
        />
      </div>
      <DataTable
        columns={usersColumns}
        rows={users}
        isLoading={usersLoading}
        error={usersError}
        emptyText="No users found."
        actions={(row) => (
          <button className="btn btn--sm" onClick={() => applyUser(row)}>Send to user</button>
        )}
      />
      {usersData && usersData.total != null && (
        <Pagination page={userPage} limit={LIMIT} total={usersData.total} onPage={setUserPage} />
      )}
    </div>
  );
}
